import { Fragment, useMemo, useState } from 'react';

import {
  CalendarOutlined,
  CloseOutlined,
  DownloadOutlined,
  MoreOutlined,
  PlusOutlined,
  StarFilled,
} from '@ant-design/icons';
import { Alert, Button, Dropdown, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { OperationBlockKey, OperationBriefForm } from 'models/OperationRequest';
import { Store } from 'models/Store';

import {
  OPS_WEEK_DAYS,
  buildOpsWeeks,
  formatClp,
  turnoHours,
} from '../data/operationCatalogs';
import AddStoresModal from './AddStoresModal';

import './OpsWeekPlanner.scss';

/** Por celda (tienda+día): cupos por bloque. */
export type WeekPlanCell = Partial<Record<OperationBlockKey, number>>;
export type WeekPlanCells = Record<string, WeekPlanCell>;

interface OpsWeekPlannerProps {
  brief: OperationBriefForm;
  stores: Store[];
  selectedStoreIds: number[];
  cells: WeekPlanCells;
  onSelectedStoresChange: (ids: number[]) => void;
  onCellsChange: (cells: WeekPlanCells) => void;
}

const cellKey = (storeId: number, day: string) => `${storeId}:${day}`;
const BLOCKS: OperationBlockKey[] = ['am', 'pm', 'full'];
const WEEKDAY_KEYS = OPS_WEEK_DAYS.map((d) => d.key);
const WEEKEND_KEYS = ['sab', 'dom'];

const hasOverlap = (cell: WeekPlanCell) => {
  const hasFull = (cell.full || 0) > 0;
  const hasPart = (cell.am || 0) > 0 || (cell.pm || 0) > 0;
  return hasFull && hasPart;
};

const cellPeople = (cell: WeekPlanCell) =>
  BLOCKS.reduce((sum, b) => sum + (cell[b] || 0), 0);

export function computePlanMetrics(
  selectedStoreIds: number[],
  cells: WeekPlanCells,
  brief: OperationBriefForm
) {
  let turnos = 0;
  let people = 0;
  let hours = 0;
  let cost = 0;
  const rate = Number(brief.rate || 0);

  selectedStoreIds.forEach((storeId) => {
    OPS_WEEK_DAYS.forEach((day) => {
      const cell = cells[cellKey(storeId, day.key)] || {};
      BLOCKS.forEach((block) => {
        const qty = cell[block] || 0;
        if (qty <= 0) return;
        turnos += 1;
        people += qty;
        hours +=
          turnoHours(brief.blocks[block].from, brief.blocks[block].to) * qty;
        // FULL = tarifa completa; AM/PM = mitad.
        const rateFactor = block === 'full' ? 1 : 0.5;
        cost += qty * rate * rateFactor;
      });
    });
  });

  return {
    turnos,
    people,
    hours,
    cost,
  };
}

function MetricsPills({
  metrics,
  i18nBase,
}: {
  metrics: ReturnType<typeof computePlanMetrics>;
  i18nBase: string;
}) {
  const { t } = useTranslation();
  const hoursLabel =
    metrics.hours % 1 ? metrics.hours.toFixed(1) : String(metrics.hours);

  return (
    <div className="ops-week-planner__metrics">
      <Tag>
        <b>{metrics.turnos}</b> {t(`${i18nBase}.metrics.turns`)}
      </Tag>
      <Tag color="cyan">
        <b>{metrics.people}</b> {t(`${i18nBase}.metrics.people`)}
      </Tag>
      <Tag>
        <b>{hoursLabel}h</b> {t(`${i18nBase}.metrics.hours`)}
      </Tag>
      <Tag color="blue" className="ops-week-planner__metric-cost">
        <b>{formatClp(metrics.cost)}</b> {t(`${i18nBase}.metrics.cost`)}
      </Tag>
    </div>
  );
}

function OpsWeekPlanner({
  brief,
  stores,
  selectedStoreIds,
  cells,
  onSelectedStoresChange,
  onCellsChange,
}: OpsWeekPlannerProps) {
  const { t } = useTranslation();
  const i18nBase =
    'scenes.requirements_operation.components.new_operation_request.week_plan';

  const [pickerOpen, setPickerOpen] = useState(false);
  const [visibleDays, setVisibleDays] = useState<string[]>([...WEEKDAY_KEYS]);

  const metrics = useMemo(
    () => computePlanMetrics(selectedStoreIds, cells, brief),
    [selectedStoreIds, cells, brief]
  );

  const selectedStores = useMemo(
    () =>
      selectedStoreIds
        .map((id) => stores.find((s) => s.id === id))
        .filter((s): s is Store => !!s),
    [selectedStoreIds, stores]
  );

  const weeks = useMemo(() => buildOpsWeeks(), []);
  const weekMeta = weeks.find((w) => w.value === brief.week) || weeks[0];
  const visibleDayDefs = OPS_WEEK_DAYS.filter((d) =>
    visibleDays.includes(d.key)
  );

  const overlapCount = useMemo(() => {
    let count = 0;
    selectedStoreIds.forEach((storeId) => {
      OPS_WEEK_DAYS.forEach((day) => {
        if (hasOverlap(cells[cellKey(storeId, day.key)] || {})) count += 1;
      });
    });
    return count;
  }, [selectedStoreIds, cells]);

  const dayTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    OPS_WEEK_DAYS.forEach((day) => {
      totals[day.key] = selectedStoreIds.reduce((sum, storeId) => {
        return sum + cellPeople(cells[cellKey(storeId, day.key)] || {});
      }, 0);
    });
    return totals;
  }, [selectedStoreIds, cells]);

  const confirmPicker = (ids: number[]) => {
    onSelectedStoresChange(ids);
    const next: WeekPlanCells = {};
    Object.entries(cells).forEach(([key, value]) => {
      const storeId = Number(key.split(':')[0]);
      if (ids.includes(storeId)) next[key] = value;
    });
    onCellsChange(next);
    setPickerOpen(false);
  };

  const patchCell = (key: string, nextCell: WeekPlanCell) => {
    const next = { ...cells };
    const empty = BLOCKS.every((b) => !nextCell[b]);
    if (empty) delete next[key];
    else next[key] = nextCell;
    onCellsChange(next);
  };

  const addBlock = (storeId: number, day: string, block: OperationBlockKey) => {
    const key = cellKey(storeId, day);
    const current = { ...(cells[key] || {}) };
    if ((current[block] || 0) > 0) return;
    current[block] = 1;
    patchCell(key, current);
  };

  const removeBlock = (
    storeId: number,
    day: string,
    block: OperationBlockKey
  ) => {
    const key = cellKey(storeId, day);
    const current = { ...(cells[key] || {}) };
    delete current[block];
    patchCell(key, current);
  };

  const setPeople = (
    storeId: number,
    day: string,
    block: OperationBlockKey,
    people: number
  ) => {
    const key = cellKey(storeId, day);
    const current = { ...(cells[key] || {}) };
    current[block] = Math.max(1, Math.min(30, people));
    patchCell(key, current);
  };

  const removeStore = (storeId: number) => {
    onSelectedStoresChange(selectedStoreIds.filter((id) => id !== storeId));
    const next: WeekPlanCells = {};
    Object.entries(cells).forEach(([key, value]) => {
      if (!key.startsWith(`${storeId}:`)) next[key] = value;
    });
    onCellsChange(next);
  };

  const clearDayColumn = (day: string) => {
    const next = { ...cells };
    selectedStoreIds.forEach((storeId) => {
      delete next[cellKey(storeId, day)];
    });
    onCellsChange(next);
  };

  /** Copia la celda de la primera fila a todas las demás de la columna. */
  const replicateDayFromFirstRow = (day: string) => {
    if (selectedStoreIds.length === 0) return;
    const sourceStoreId = selectedStoreIds[0];
    const source = { ...(cells[cellKey(sourceStoreId, day)] || {}) };
    const empty = BLOCKS.every((b) => !source[b]);
    const next = { ...cells };

    selectedStoreIds.forEach((storeId) => {
      const key = cellKey(storeId, day);
      if (empty) delete next[key];
      else next[key] = { ...source };
    });

    onCellsChange(next);
  };

  /** Copia toda la semana de esta sala a las demás tiendas del tablero. */
  const replicateStoreRowToOthers = (sourceStoreId: number) => {
    if (selectedStoreIds.length < 2) return;
    const next = { ...cells };

    selectedStoreIds.forEach((storeId) => {
      if (storeId === sourceStoreId) return;
      OPS_WEEK_DAYS.forEach((day) => {
        const sourceKey = cellKey(sourceStoreId, day.key);
        const targetKey = cellKey(storeId, day.key);
        const source = cells[sourceKey];
        if (!source || BLOCKS.every((b) => !source[b])) {
          delete next[targetKey];
        } else {
          next[targetKey] = { ...source };
        }
      });
    });

    onCellsChange(next);
  };

  const toggleVisibleDay = (dayKey: string) => {
    setVisibleDays((prev) => {
      if (prev.includes(dayKey)) {
        if (prev.length === 1) return prev;
        return prev.filter((d) => d !== dayKey);
      }
      return [...prev, dayKey];
    });
  };

  const setWeekPreset = (preset: 'all' | 'weekend') => {
    setVisibleDays(preset === 'all' ? [...WEEKDAY_KEYS] : [...WEEKEND_KEYS]);
  };

  const isWeekendPreset =
    visibleDays.length === 2 &&
    visibleDays.includes('sab') &&
    visibleDays.includes('dom');
  const isAllPreset = visibleDays.length === 7;

  const storeLoc = (store: Store) =>
    store.regionKind === 'CAPITAL'
      ? store.commune || store.regionLabel
      : store.regionLabel;

  return (
    <div className="ops-week-planner">
      <div className="ops-week-planner__card">
        <div className="ops-week-planner__header">
          <div className="ops-week-planner__header-left">
            <span className="ops-week-planner__step-badge">2</span>
            <div>
              <div className="ops-week-planner__title">
                {t(`${i18nBase}.title`)}
              </div>
              <div className="ops-week-planner__sub">
                {t(`${i18nBase}.subtitle`)}
              </div>
            </div>
          </div>

          <MetricsPills metrics={metrics} i18nBase={i18nBase} />

          <div className="ops-week-planner__header-actions">
            <Button icon={<DownloadOutlined />}>
              {t(`${i18nBase}.buttons.import_export`)}
            </Button>
            {selectedStores.length > 0 && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setPickerOpen(true)}
              >
                {t(`${i18nBase}.buttons.add_stores`)}
              </Button>
            )}
          </div>
        </div>

        {overlapCount > 0 && (
          <Alert
            type="warning"
            showIcon
            className="ops-week-planner__overlap"
            message={t(`${i18nBase}.overlap_warning`, { count: overlapCount })}
          />
        )}

        {selectedStores.length === 0 ? (
          <div className="ops-week-planner__empty">
            <div className="ops-week-planner__empty-icon">
              <CalendarOutlined />
            </div>
            <div className="ops-week-planner__empty-title">
              {t(`${i18nBase}.empty.title`)}
            </div>
            <p className="ops-week-planner__empty-text">
              {t(`${i18nBase}.empty.description`)}
            </p>
            <div className="ops-week-planner__empty-actions">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setPickerOpen(true)}
              >
                {t(`${i18nBase}.buttons.add_stores`)}
              </Button>
              <Button icon={<DownloadOutlined />}>
                {t(`${i18nBase}.buttons.import_template`)}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="ops-week-planner__days-bar">
              <span className="ops-week-planner__days-label">
                {t(`${i18nBase}.visible_days`)}
              </span>
              <div className="ops-week-planner__days-toggles">
                {OPS_WEEK_DAYS.map((day) => {
                  const on = visibleDays.includes(day.key);
                  return (
                    <button
                      key={day.key}
                      type="button"
                      className={`ops-week-planner__day-toggle${
                        on ? ' ops-week-planner__day-toggle--on' : ''
                      }`}
                      onClick={() => toggleVisibleDay(day.key)}
                    >
                      {on && <span className="ops-week-planner__day-dot" />}
                      {day.short}
                    </button>
                  );
                })}
              </div>
              <div className="ops-week-planner__day-presets">
                <button
                  type="button"
                  className={`ops-week-planner__preset${
                    isAllPreset ? ' ops-week-planner__preset--on' : ''
                  }`}
                  onClick={() => setWeekPreset('all')}
                >
                  {t(`${i18nBase}.presets.all`)}
                </button>
                <button
                  type="button"
                  className={`ops-week-planner__preset${
                    isWeekendPreset ? ' ops-week-planner__preset--on' : ''
                  }`}
                  onClick={() => setWeekPreset('weekend')}
                >
                  {t(`${i18nBase}.presets.weekend`)}
                </button>
              </div>
            </div>

            <div className="ops-week-planner__matrix">
              <div
                className="ops-week-planner__grid"
                style={{
                  gridTemplateColumns: `240px repeat(${visibleDayDefs.length}, minmax(148px, 1fr))`,
                }}
              >
                <div className="ops-week-planner__corner">
                  {t(`${i18nBase}.matrix.store`)}
                </div>
                {visibleDayDefs.map((day) => (
                  <div key={day.key} className="ops-week-planner__day-head">
                    <div className="ops-week-planner__day-head-text">
                      <span className="ops-week-planner__day-name">
                        {day.short}
                      </span>
                      <span className="ops-week-planner__day-date">
                        {weekMeta.dates[day.key as keyof typeof weekMeta.dates]}
                      </span>
                    </div>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'replicate',
                            label: t(`${i18nBase}.matrix.replicate_from_first`),
                            disabled: selectedStoreIds.length < 2,
                            onClick: () => replicateDayFromFirstRow(day.key),
                          },
                          {
                            key: 'clear',
                            label: t(`${i18nBase}.matrix.clear_day`),
                            onClick: () => clearDayColumn(day.key),
                          },
                        ],
                      }}
                      trigger={['click']}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<MoreOutlined />}
                        aria-label={t(`${i18nBase}.matrix.day_menu`)}
                      />
                    </Dropdown>
                  </div>
                ))}

                {selectedStores.map((store) => (
                  <Fragment key={store.id}>
                    <div className="ops-week-planner__store-cell">
                      <div className="ops-week-planner__store-top">
                        <div className="ops-week-planner__store-name">
                          {store.isVip && (
                            <StarFilled className="ops-week-planner__vip" />
                          )}
                          <span>{store.name}</span>
                        </div>
                        <Dropdown
                          menu={{
                            items: [
                              {
                                key: 'replicate-row',
                                label: t(
                                  `${i18nBase}.matrix.replicate_row_to_others`
                                ),
                                disabled: selectedStoreIds.length < 2,
                                onClick: () =>
                                  replicateStoreRowToOthers(store.id),
                              },
                              {
                                key: 'remove',
                                danger: true,
                                label: t(`${i18nBase}.buttons.remove_store`),
                                onClick: () => removeStore(store.id),
                              },
                            ],
                          }}
                          trigger={['click']}
                        >
                          <Button
                            type="text"
                            size="small"
                            icon={<MoreOutlined />}
                            aria-label={t(`${i18nBase}.matrix.store_menu`)}
                          />
                        </Dropdown>
                      </div>
                      <div className="ops-week-planner__store-meta">
                        {store.chain} · {storeLoc(store)}
                      </div>
                      {store.isVip && (
                        <span className="ops-week-planner__vip-badge">VIP</span>
                      )}
                    </div>

                    {visibleDayDefs.map((day) => {
                      const key = cellKey(store.id, day.key);
                      const cell = cells[key] || {};
                      const overlap = hasOverlap(cell);
                      return (
                        <div
                          key={key}
                          className={`ops-week-planner__day-cell${
                            overlap
                              ? ' ops-week-planner__day-cell--overlap'
                              : ''
                          }`}
                        >
                          {BLOCKS.map((block) => {
                            const people = cell[block] || 0;
                            const active = people > 0;
                            const range = `${brief.blocks[block].from}–${brief.blocks[block].to}`;
                            if (active) {
                              return (
                                <div
                                  key={block}
                                  className={`ops-week-planner__slot ops-week-planner__slot--${block} ops-week-planner__slot--on`}
                                >
                                  <div className="ops-week-planner__slot-top">
                                    <span className="ops-week-planner__slot-tag">
                                      {block.toUpperCase()}
                                    </span>
                                    <span className="ops-week-planner__slot-range">
                                      {range}
                                    </span>
                                    <button
                                      type="button"
                                      className="ops-week-planner__slot-remove"
                                      onClick={() =>
                                        removeBlock(store.id, day.key, block)
                                      }
                                      aria-label={t(
                                        `${i18nBase}.matrix.remove_block`
                                      )}
                                    >
                                      <CloseOutlined />
                                    </button>
                                  </div>
                                  <div className="ops-week-planner__counter">
                                    <button
                                      type="button"
                                      disabled={people <= 1}
                                      onClick={() =>
                                        setPeople(
                                          store.id,
                                          day.key,
                                          block,
                                          people - 1
                                        )
                                      }
                                    >
                                      −
                                    </button>
                                    <span>{people}</span>
                                    <button
                                      type="button"
                                      disabled={people >= 30}
                                      onClick={() =>
                                        setPeople(
                                          store.id,
                                          day.key,
                                          block,
                                          people + 1
                                        )
                                      }
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <Tooltip
                                key={block}
                                title={t(`${i18nBase}.matrix.add_block`, {
                                  name: t(`${i18nBase}.blocks.${block}`),
                                  range,
                                })}
                              >
                                <button
                                  type="button"
                                  className={`ops-week-planner__slot ops-week-planner__slot--${block}`}
                                  onClick={() =>
                                    addBlock(store.id, day.key, block)
                                  }
                                >
                                  <span>
                                    <b>{block.toUpperCase()}</b> {range}
                                  </span>
                                  <PlusOutlined />
                                </button>
                              </Tooltip>
                            );
                          })}
                        </div>
                      );
                    })}
                  </Fragment>
                ))}

                <div className="ops-week-planner__total-label">
                  {t(`${i18nBase}.matrix.day_total`)}
                </div>
                {visibleDayDefs.map((day) => {
                  const total = dayTotals[day.key] || 0;
                  return (
                    <div
                      key={`total-${day.key}`}
                      className={`ops-week-planner__total-cell${
                        total > 0 ? ' ops-week-planner__total-cell--on' : ''
                      }`}
                    >
                      {total}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      <AddStoresModal
        open={pickerOpen}
        stores={stores}
        selectedIds={selectedStoreIds}
        onCancel={() => setPickerOpen(false)}
        onConfirm={confirmPicker}
      />
    </div>
  );
}

export default OpsWeekPlanner;
